import { useEffect, useMemo, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import MeetingDiscussionFormDialog from "@/components/dialogs/MeetingDiscussionFormDialog";
import api, { fileUrl, downloadUrl, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Users, Building2, CalendarClock, Paperclip, Eye, Download, FileText, ClipboardCheck, Handshake } from "lucide-react";

const STATUS_TABS = ["All", "Open", "In Progress", "Completed"];
const STATUS_STYLES = {
  Open: "bg-[#F7FEE7] text-[#00823B]",
  "In Progress": "bg-amber-50 text-amber-700",
  Completed: "bg-[#4B5563]/10 text-[#4B5563]",
};

export default function MeetingDiscussions() {
  const [discussions, setDiscussions] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [statusFilter, setStatusFilter] = useState("All");
  const [formOpen, setFormOpen] = useState(false);
  const [editingDiscussion, setEditingDiscussion] = useState(null);

  const load = () => api.get("/meeting-discussions").then((r) => setDiscussions(r.data)).catch(() => {});

  useEffect(() => {
    load();
    api.get("/employees/directory").then((r) => setEmployees(r.data)).catch(() => setEmployees([]));
  }, []);

  const employeesMap = useMemo(() => Object.fromEntries(employees.map((e) => [e.id, e.name])), [employees]);

  const filtered = useMemo(
    () => (statusFilter === "All" ? discussions : discussions.filter((d) => d.status === statusFilter)),
    [discussions, statusFilter]
  );

  const deleteDiscussion = async (id) => {
    try {
      await api.delete(`/meeting-discussions/${id}`);
      toast.success("Meeting discussion removed");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const openAdd = () => {
    setEditingDiscussion(null);
    setFormOpen(true);
  };

  const openEdit = (d) => {
    setEditingDiscussion(d);
    setFormOpen(true);
  };

  return (
    <DashboardLayout title="Meeting Discussions">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Handshake className="h-4 w-4 text-[#00823B]" />
          <p className="text-sm text-[#4B5563]">Non-client meetings — suppliers, vendors, government and partners.</p>
        </div>
        <Button data-testid="add-meeting-discussion-button" onClick={openAdd} className="bg-[#76C121] hover:bg-[#5C9819] text-white">
          <Plus className="h-4 w-4 mr-1.5" /> Log Meeting
        </Button>
      </div>

      <Tabs value={statusFilter} onValueChange={setStatusFilter} className="mb-5">
        <TabsList data-testid="meeting-discussion-status-tabs">
          {STATUS_TABS.map((s) => (
            <TabsTrigger key={s} value={s} data-testid={`meeting-discussion-status-tab-${s.replace(/\s/g, "-")}`}>{s}</TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {filtered.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-center bg-white border border-[#E2E8F0] rounded-lg" data-testid="meeting-discussions-empty-state">
          <Handshake className="h-6 w-6 text-[#4B5563]/40 mb-2" />
          <p className="text-sm text-[#4B5563]">No meeting discussions logged yet. Click "Log Meeting" to add the first one.</p>
        </div>
      )}

      <div className="space-y-4">
        {filtered.map((d) => (
          <DiscussionCard key={d.id} discussion={d} employeesMap={employeesMap} onEdit={() => openEdit(d)} onDelete={() => deleteDiscussion(d.id)} />
        ))}
      </div>

      <MeetingDiscussionFormDialog open={formOpen} onOpenChange={setFormOpen} discussion={editingDiscussion} employees={employees} onSaved={load} />
    </DashboardLayout>
  );
}

function DiscussionCard({ discussion: d, employeesMap, onEdit, onDelete }) {
  const internalNames = (d.participants_internal || []).map((id) => employeesMap[id]).filter(Boolean);
  const allParticipants = [...internalNames, ...(d.participants_external || [])];

  return (
    <div data-testid={`meeting-discussion-row-${d.id}`} className="bg-white border border-[#E2E8F0] rounded-lg p-5">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-heading text-sm font-semibold text-[#1C1C1E]">{d.title}</h3>
            <span className={`text-[10px] font-medium uppercase tracking-wide px-2 py-0.5 rounded ${STATUS_STYLES[d.status] || STATUS_STYLES.Open}`}>{d.status}</span>
          </div>
          <div className="flex items-center gap-4 mt-1.5 flex-wrap text-xs text-[#4B5563]">
            <span className="flex items-center gap-1"><CalendarClock className="h-3 w-3" /> {d.meeting_datetime ? new Date(d.meeting_datetime).toLocaleString() : "—"}</span>
            {d.organization && <span className="flex items-center gap-1"><Building2 className="h-3 w-3" /> {d.organization}</span>}
            {allParticipants.length > 0 && <span className="flex items-center gap-1"><Users className="h-3 w-3" /> {allParticipants.join(", ")}</span>}
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <button data-testid={`meeting-discussion-edit-${d.id}`} onClick={onEdit} className="h-7 w-7 flex items-center justify-center rounded-md text-[#4B5563] hover:bg-[#F8F9FA] transition-colors duration-200">
            <Pencil className="h-3.5 w-3.5" />
          </button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button data-testid={`meeting-discussion-delete-${d.id}`} className="h-7 w-7 flex items-center justify-center rounded-md text-red-600 hover:bg-red-50 transition-colors duration-200">
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete "{d.title}"?</AlertDialogTitle>
                <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={onDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {(d.discussion_notes || d.key_decisions || d.action_items) && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3 pt-3 border-t border-[#E2E8F0]">
          {d.discussion_notes && (
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wide text-[#4B5563] mb-1">Discussion Notes</p>
              <p className="text-sm text-[#1C1C1E]/80 whitespace-pre-wrap line-clamp-4">{d.discussion_notes}</p>
            </div>
          )}
          {d.key_decisions && (
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wide text-[#4B5563] mb-1">Key Decisions</p>
              <p className="text-sm text-[#1C1C1E]/80 whitespace-pre-wrap line-clamp-4">{d.key_decisions}</p>
            </div>
          )}
          {d.action_items && (
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wide text-[#4B5563] mb-1 flex items-center gap-1"><ClipboardCheck className="h-3 w-3" /> Action Items</p>
              <p className="text-sm text-[#1C1C1E]/80 whitespace-pre-wrap line-clamp-4">{d.action_items}</p>
            </div>
          )}
        </div>
      )}

      <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#E2E8F0] flex-wrap gap-2">
        <span className="text-xs text-[#4B5563]">
          {d.follow_up_date && <>Follow-up: {d.follow_up_date} · </>}by {d.created_by}
        </span>
        {d.attachments?.length > 0 && (
          <div className="flex items-center gap-2.5" data-testid={`meeting-discussion-attachments-${d.id}`}>
            <Paperclip className="h-3.5 w-3.5 text-[#4B5563]" />
            {d.attachments.map((a) => (
              <span key={a.file_path} className="flex items-center gap-1.5">
                <FileText className="h-3 w-3 text-[#4B5563]" />
                <span className="text-xs text-[#4B5563] max-w-[100px] truncate">{a.original_filename}</span>
                <a href={fileUrl(a.file_path)} target="_blank" rel="noreferrer" title="View"><Eye className="h-3.5 w-3.5 text-[#4B5563]" /></a>
                <a href={downloadUrl(a.file_path, a.original_filename)} title="Download"><Download className="h-3.5 w-3.5 text-[#00823B]" /></a>
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
